import {Component, React, observer, resolveCss} from '$src/handy'
import * as css from './Table.css'
import {ResearchStateTab, IStore} from '$src/bootstrap/Store'
import ReactTable, {Column} from 'react-table'
import {getSnapshot} from 'mobx-state-tree'
import HideColumnIcon from 'react-icons/lib/fa/close'
import {includes} from 'lodash'
import {DateTime} from 'luxon'
import OldTableOfComparableSales from './OldTableOfComparableSales'

interface Props {
  css?: any
  activeTabState: typeof ResearchStateTab.Type
  setHoveredItemIds: (ids: {[id: number]: true}) => void
  hoveredItemIds: {[id: number]: true}
  setActiveItemIds: (ids: {[id: number]: true}) => void
  activeItemIds: {[id: number]: true}
  activeItemIdsArray: string[]
  dataType: string
}

interface State {
  apiData: any[]
}

const classes = resolveCss(css, {})

@observer
export default class TableOfComparableSales extends Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context)
    this.state = {
      apiData: [],
    }
  }

  getTrProps = (_: unknown, row: $FixMe) => {
    if (!row) return {}
    const {original} = row

    return {
      onClick: () => {
        this.props.setActiveItemIds({[original.id]: true})
      },
      onMouseOver: () => {
        if (original.municipality) {
          this.props.setHoveredItemIds({[original.id]: true})
        }
      },
      ...classes(
        original.selected === true && 'enabled',
        this.props.activeItemIds[original.id] === true && 'activeRow',
        this.props.hoveredItemIds[original.id] === true && 'hoveredRow',
      ),
    }
  }

  render() {
    const {props} = this
    const foundItems = getSnapshot(props.activeTabState.foundItems)
    const {byId} = foundItems

    const premiseList = foundItems.list
      .map((id: string) => ({
        ...byId[id],
        selected: foundItems.selectedIds[id] === true,
      }))
      .filter((item: {dataType: string}) => item.dataType === 'premise')

    const oldDataList = foundItems.list.map((id: string) => ({
      ...byId[id],
      selected: foundItems.selectedIdsOldData[id] === true,
    }))

    const markerlist = props.activeItemIdsArray.map((item: {id: string}) => ({
      ...byId[item.id],
      selected:
        foundItems.selectedIdsOldData.size > 0
          ? foundItems.selectedIdsOldData[item.id] === true
          : foundItems.selectedIds[item.id] === true,
    }))

    const dataList = props.dataType === 'oldData' ? oldDataList : premiseList
    const dataSource = markerlist.length > 0 ? markerlist : dataList

    const hiddenCols = getSnapshot(
      this.store.hiddenColumnsInComparableSalesView.byId,
    )

    if (dataSource) {
      if (props.dataType === 'oldData') {
        return (
          <OldTableOfComparableSales
            activeTabState={props.activeTabState}
            activeItemIds={this.props.activeItemIds}
            hoveredItemIds={this.props.hoveredItemIds}
            setHoveredItemIds={this.props.setHoveredItemIds}
            setActiveItemIds={this.props.setActiveItemIds}
            markerlist={markerlist}
          />
        )
      } else {
        return (
          <ReactTable
            className={css.table}
            data={dataSource}
            defaultFilterMethod={defaultFilterMethod}
            showPagination={true}
            getTrProps={this.getTrProps}
            loading={props.activeTabState.foundItems.queried !== true}
            defaultPageSize={premiseList.length}
            pageSize={props.activeTabState.getSelectedPageSize() || 10}
            pageSizeOptions={[
              10,
              20,
              50,
              ...(premiseList.length > 50 ? [premiseList.length] : []),
            ]}
            onPageSizeChange={pageSize =>
              props.activeTabState.setSelectedPageSize(pageSize)
            }
            multiSort={false}
            style={{
              height: '110vh',
            }}
            columns={[
              {
                Header: '',
                accessor: 'selected',
                sortable: true,
                maxWidth: 40,
                className: css.disabledEnabledCell,
                Cell: ({original}) => {
                  if (original.id) {
                    return (
                      <label
                        className={css.checkBoxLabel}
                        onClick={e => e.stopPropagation()}
                        key={'checkbox-label-' + original.id}
                      >
                        <input
                          type="checkbox"
                          checked={original.selected}
                          key={'checkbox-' + original.id}
                          title="Hold ALT/OPTION to select/deselect other sales in the same address"
                          onChange={e => {
                            if (e.nativeEvent.altKey) {
                              props.activeTabState.batchSelectOrUnselectByAddress(
                                original.id,
                                !original.selected,
                              )
                            } else {
                              props.activeTabState.setSelectedForId(
                                original.id,
                                !original.selected,
                              )
                            }
                          }}
                        />
                      </label>
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Property type'),
                accessor: 'propertyType',
                sortable: true,
                show: hiddenCols['propertyType'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.propertyType) {
                    return format(original.propertyType, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Living area'),
                accessor: 'livingArea',
                sortable: true,
                show: hiddenCols['livingArea'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.livingArea) {
                    return format(original.livingArea, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Year of construction'),
                accessor: 'yearOfConstruction',
                sortable: true,
                show: hiddenCols['yearOfConstruction'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.yearOfConstruction) {
                    return format(original.yearOfConstruction, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('# of rooms'),
                accessor: 'numberOfRooms',
                sortable: true,
                show: hiddenCols['numberOfRooms'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.numberOfRooms) {
                    return format(original.numberOfRooms, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Newly built'),
                accessor: 'newlyBuilt',
                sortable: true,
                show: hiddenCols['newlyBuilt'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.newlyBuilt) {
                    return format(original.newlyBuilt, original)
                  } else if (original.id) {
                    return 'No'
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Condition'),
                accessor: 'condition',
                sortable: true,
                show: hiddenCols['condition'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.condition) {
                    return format(original.condition, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Plot owned'),
                accessor: 'plotOwned',
                sortable: true,
                show: hiddenCols['plotOwned'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.plotOwned) {
                    return format(original.plotOwned, original)
                  } else if (original.id) {
                    return 'No'
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Has elevator'),
                accessor: 'hasElevator',
                sortable: true,
                show: hiddenCols['hasElevator'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.hasElevator) {
                    return format(original.hasElevator, original)
                  } else if (original.id) {
                    return 'No'
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Has sauna'),
                accessor: 'hasSauna',
                sortable: true,
                show: hiddenCols['hasSauna'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.hasSauna) {
                    return format(original.hasSauna, original)
                  } else if (original.id) {
                    return 'No'
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Apartment type exact'),
                accessor: 'apartmentTypeExact',
                sortable: true,
                show: hiddenCols['apartmentTypeExact'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.apartmentTypeExact) {
                    return format(original.apartmentTypeExact, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Rooms description'),
                accessor: 'roomsDescription',
                sortable: true,
                show: hiddenCols['roomsDescription'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.roomsDescription) {
                    return format(original.roomsDescription, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Floor number'),
                accessor: 'floorNumber',
                sortable: true,
                show: hiddenCols['floorNumber'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.floorNumber) {
                    return format(original.floorNumber, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price sqm adjusted'),
                accessor: 'priceSqmAdjusted',
                sortable: true,
                show: hiddenCols['priceSqmAdjusted'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqmAdjusted) {
                    return format(
                      original.priceSqmAdjusted.toFixed(0),
                      original,
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price free of dept adjusted'),
                accessor: 'priceFreeOfDeptAdjusted',
                sortable: true,
                show: hiddenCols['priceFreeOfDeptAdjusted'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceFreeOfDeptAdjusted) {
                    return format(
                      original.priceFreeOfDeptAdjusted.toFixed(0),
                      original,
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price sqm'),
                accessor: 'priceSqm',
                sortable: true,
                show: hiddenCols['priceSqm'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqm) {
                    return format(original.priceSqm.toFixed(0), original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price free of dept'),
                accessor: 'priceFreeOfDept',
                sortable: true,
                show: hiddenCols['priceFreeOfDept'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceFreeOfDept) {
                    return format(original.priceFreeOfDept.toFixed(0), original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Floor number'),
                accessor: 'floorNumber',
                sortable: true,
                show: hiddenCols['floorNumber'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.floorNumber) {
                    return format(original.floorNumber, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price sqm adjusted'),
                accessor: 'priceSqmAdjusted',
                sortable: true,
                show: hiddenCols['priceSqmAdjusted'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqmAdjusted) {
                    return format(
                      original.priceSqmAdjusted.toFixed(0),
                      original,
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price free of dept adjusted'),
                accessor: 'priceFreeOfDeptAdjusted',
                sortable: true,
                show: hiddenCols['priceFreeOfDeptAdjusted'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceFreeOfDeptAdjusted) {
                    return format(
                      original.priceFreeOfDeptAdjusted.toFixed(0),
                      original,
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price sqm'),
                accessor: 'priceSqm',
                sortable: true,
                show: hiddenCols['priceSqm'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqm) {
                    return format(original.priceSqm.toFixed(0), original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price free of dept'),
                accessor: 'priceFreeOfDept',
                sortable: true,
                show: hiddenCols['priceFreeOfDept'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceFreeOfDept) {
                    return format(original.priceFreeOfDept.toFixed(0), original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Municipality'),
                accessor: 'municipality',
                sortable: true,
                show: hiddenCols['municipality'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.municipality) {
                    return format(original.municipality, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Postal code'),
                accessor: 'postalCode',
                sortable: true,
                show: hiddenCols['postalCode'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.postalCode) {
                    return format(original.postalCode, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Street address'),
                accessor: 'streetAddress',
                sortable: true,
                show: hiddenCols['streetAddress'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.streetAddress) {
                    return format(original.streetAddress, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Length of sale'),
                accessor: 'sellLength',
                sortable: true,
                show: hiddenCols['sellLength'] !== true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.sellLength) {
                    return format(original.sellLength, original)
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Date of sale'),
                accessor: 'dateOfSale',
                sortable: true,
                show: hiddenCols['dateOfSale'] !== true,
                filterable: true,
                sortMethod: (a: string, b: string) => {
                  const aT = DateTime.fromISO(a)
                  const bT = DateTime.fromISO(b)
                  return aT > bT ? 1 : aT < bT ? -1 : 0
                },
                Cell: ({original}: {original: $FixMe}) => {
                  if (original.dateOfSale) {
                    return DateTime.fromISO(original.dateOfSale).toFormat(
                      'dd.LL.yyyy',
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Location'),
                accessor: 'location',
                store: this.store,
                width: 90,
                sortable: true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.location) {
                    return (
                      <div className={css.location}>
                        <div className={css.pair}>
                          <span>
                            {parseFloat(original.location.lat).toFixed(6)}
                          </span>
                          <span>
                            {parseFloat(original.location.long).toFixed(6)}
                          </span>
                        </div>
                      </div>
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price 1Q ago'),
                accessor: 'priceSqmAdjusted1QuarterAgo',
                store: this.store,
                width: 90,
                sortable: true,
                filterable: true,
                Cell: ({original}) => {
                  const value = original.hasOwnProperty(
                    'priceSqmAdjusted1QuarterAgo',
                  )
                    ? original.priceSqmAdjusted1QuarterAgo
                    : null
                  if (value != null) {
                    return <span>{parseFloat(value).toFixed(0)}</span>
                  } else {
                    return null
                  }
                },
              },

              {
                Header: ColumnHeader('Price 2Q ago'),
                accessor: 'priceSqmAdjusted2QuartersAgo',
                store: this.store,
                width: 90,
                sortable: true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqmAdjusted2QuartersAgo) {
                    return (
                      <span>
                        {parseFloat(
                          original.priceSqmAdjusted2QuartersAgo,
                        ).toFixed(0)}
                      </span>
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price 3Q ago'),
                accessor: 'priceSqmAdjusted3QuartersAgo',
                store: this.store,
                width: 90,
                sortable: true,
                Cell: ({original}) => {
                  if (original.priceSqmAdjusted3QuartersAgo) {
                    return (
                      <span>
                        {parseFloat(
                          original.priceSqmAdjusted3QuartersAgo,
                        ).toFixed(0)}
                      </span>
                    )
                  } else {
                    return null
                  }
                },
              },
              {
                Header: ColumnHeader('Price 4Q ago'),
                accessor: 'priceSqmAdjusted4QuartersAgo',
                store: this.store,
                width: 90,
                sortable: true,
                filterable: true,
                Cell: ({original}) => {
                  if (original.priceSqmAdjusted4QuartersAgo) {
                    return (
                      <span>
                        {parseFloat(
                          original.priceSqmAdjusted4QuartersAgo,
                        ).toFixed(0)}
                      </span>
                    )
                  } else {
                    return null
                  }
                },
              },
            ]}
          />
        )
      }
    } else {
      console.log('No data source for table')
      return null
    }
  }
}

const format = (s: unknown, opts: {toFixed?: number}) =>
  typeof s === 'string'
    ? s
    : typeof s === 'number'
    ? typeof opts.toFixed === 'number'
      ? s.toFixed(opts.toFixed)
      : s
    : typeof s === 'boolean'
    ? s
      ? 'Yes'
      : 'No'
    : s === null
    ? 'Unkown'
    : s === undefined
    ? 'Unkown'
    : JSON.stringify(s)

const ColumnHeader = (headerText: string) => ({
  column,
}: {
  column: {store: IStore; id: string}
}) => {
  const store = column.store

  return (
    <div className={css.columnHeader}>
      {headerText}
      <div
        className={css.columnHeaderCloseButton}
        onClick={() => {
          store.hiddenColumnsInComparableSalesView.add(column.id)
        }}
      >
        <HideColumnIcon />
      </div>
    </div>
  )
}

export const defaultFilterMethod = (filter: any, row: any, column: Column) => {
  const id = filter.pivotId || filter.id
  return row[id] !== undefined
    ? includes(String(row[id]).toLowerCase(), filter.value.toLowerCase())
    : true
}

export function booleanFilterMethod(accessor: string) {
  return (filter: $FixMe, row: $FixMe) => {
    if (typeof filter.value !== 'string' || filter.value.length === 0)
      return true
    if (filter.value[0].toLowerCase() === 'y') {
      return row[accessor] === true
    }
    if (filter.value[0].toLowerCase() === 'n') {
      return row[accessor] === false
    }
    return true
  }
}



// WEBPACK FOOTER //
// ./src/comparableSales/TableOfComparableSales.tsx